import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';

const SearchableSelect = ({ 
  label, 
  value, 
  onChange, 
  options, 
  placeholder = "Select an option...",
  emptyLabel = "all"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = (options || []).filter(option =>
    option && option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
    
    // Close the filters panel after selection by triggering a custom event
    // This will be handled by the parent component
    if (option !== '') {
      const event = new CustomEvent('filterSelected');
      window.dispatchEvent(event);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-secondary mb-2 font-wix">
          {label}
        </label>
      )}
      
      <div 
        className="w-full px-4 py-3 border border-gray-200 rounded-2xl bg-white cursor-pointer flex items-center justify-between transition-all duration-300 hover:border-primary focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent"
        onClick={toggleDropdown}
      >
        <span className={`font-wix ${!value ? 'text-gray-400' : 'text-secondary'}`}>
          {value || emptyLabel}
        </span>
        <div className="flex items-center space-x-2">
          {value && (
            <button
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              type="button"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <ChevronDown 
            className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'transform rotate-180' : ''
            }`}
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200">
            <input
              ref={inputRef}
              type="text"
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-wix text-sm"
              placeholder={placeholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {/* Empty option */}
            <div
              className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors font-wix text-sm"
              onClick={() => handleSelect('')}
            >
              {emptyLabel}
            </div>

            {/* Filtered options */}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={index}
                  className={`px-4 py-3 hover:bg-primary/10 cursor-pointer transition-colors font-wix text-sm ${
                    value === option ? 'bg-primary/5 text-primary font-medium' : 'text-secondary'
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  {option}
                </div>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-gray-400 font-wix text-sm">
                {searchTerm ? (
                  <>
                    <p className="mb-1">No results</p>
                    <p className="text-xs">"{searchTerm}" is not in the list</p>
                  </>
                ) : options && options.length === 0 ? (
                  <>
                    <p className="mb-1">No options available</p>
                    <p className="text-xs">Please check your connection or add items first</p>
                  </>
                ) : (
                  <p className="mb-1">No results</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;

